/** @jsxImportSource preact */
import { h } from 'preact';
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import * as echarts from 'echarts';
import { getCategoryColor, getChartTheme, detectDarkMode, getResponsiveConfig, loadTheme } from '@/lib/theme';
import { getDomainColor, getSubdomainPaletteColor, DOMAIN_ORDER } from '@/data/incidentDomainColors';
import type { IncidentChartConfig } from '@/data/chartConfigs';

export interface IncidentChartProps {
  config: IncidentChartConfig;
  apiBase: string;
  containerId: string;
}

interface IncidentApiResponse {
  chartType: string;
  totalIncidents: number;
  totalIncidentsAll: number;
  years?: string[];
  domains?: string[];
  subdomains?: string[];
  series?: Array<{ name: string; data: number[] }>;
  data?: Array<{ name: string; value: number }>;
  domainTotals?: number[];
  lastUpdated: string;
  // Filter options
  entities?: string[];
  intents?: string[];
  severityFields?: Array<{ key: string; label: string }>;
}

const MOBILE_BREAKPOINT = 640;

function niceMax(value: number): number {
  if (value <= 0) return 1;
  const rawInterval = value / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawInterval)));
  const normalized = rawInterval / magnitude;
  let tickInterval: number;
  if (normalized <= 1.5) tickInterval = magnitude;
  else if (normalized <= 3.5) tickInterval = 2 * magnitude;
  else if (normalized <= 7.5) tickInterval = 5 * magnitude;
  else tickInterval = 10 * magnitude;
  return Math.ceil(value / tickInterval) * tickInterval;
}

export function IncidentChart({ config, apiBase, containerId }: IncidentChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const theme = loadTheme();

  // Filter state
  const [yearFilter, setYearFilter] = useState<string[]>([]);
  const [domainFilter, setDomainFilter] = useState<string[]>([]);
  const [subdomainFilter, setSubdomainFilter] = useState<string[]>([]);
  const [entityFilter, setEntityFilter] = useState<string[]>([]);
  const [intentFilter, setIntentFilter] = useState<string[]>([]);
  const [severityFilters, setSeverityFilters] = useState<Record<string, number>>({});
  const [severityOpen, setSeverityOpen] = useState(false);

  // Data state
  const [apiData, setApiData] = useState<IncidentApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dropdown options
  const [yearOptions, setYearOptions] = useState<string[]>([]);
  const [domainOptions, setDomainOptions] = useState<string[]>([]);
  const [subdomainOptions, setSubdomainOptions] = useState<string[]>([]);
  const [entityOptions, setEntityOptions] = useState<string[]>([]);
  const [intentOptions, setIntentOptions] = useState<string[]>([]);
  const [severityFields, setSeverityFields] = useState<Array<{ key: string; label: string }>>([]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.set('chartType', config.apiChartType);
    if (yearFilter.length) params.set('year', yearFilter.join(','));
    if (domainFilter.length) params.set('domain', domainFilter.join(','));
    if (subdomainFilter.length) params.set('subdomain', subdomainFilter.join(','));
    if (entityFilter.length) params.set('entity', entityFilter.join(','));
    if (intentFilter.length) params.set('intent', intentFilter.join(','));

    for (const [key, val] of Object.entries(severityFilters)) {
      if (val > 0) params.set(key, String(val));
    }

    try {
      const resp = await fetch(`${apiBase}/data/incidents?${params}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data: IncidentApiResponse = await resp.json();
      setApiData(data);

      // Set dropdown options from first response
      if (data.years) setYearOptions(data.years);
      if (data.domains) setDomainOptions(data.domains);
      if (data.subdomains) setSubdomainOptions(data.subdomains);
      if (data.entities) setEntityOptions(data.entities);
      if (data.intents) setIntentOptions(data.intents);
      if (data.severityFields) setSeverityFields(data.severityFields);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [apiBase, config.apiChartType, yearFilter, domainFilter, subdomainFilter, entityFilter, intentFilter, severityFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Render chart
  useEffect(() => {
    if (!apiData || !chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const isDark = detectDarkMode();
    const chartTheme = getChartTheme(isDark);
    const containerWidth = chartRef.current.clientWidth;
    const isMobile = containerWidth < MOBILE_BREAKPOINT;

    let option: echarts.EChartsOption;

    switch (config.visualType) {
      case 'vertical-bar':
        option = buildVerticalBarOption(apiData, chartTheme, isMobile, config);
        break;
      case 'horizontal-bar':
        option = buildHorizontalBarOption(apiData, chartTheme, isMobile);
        break;
      case 'pie':
        option = buildPieOption(apiData, chartTheme, isMobile);
        break;
      default:
        return;
    }

    chartInstance.current.setOption(option, { notMerge: true });
  }, [apiData, config]);

  // Resize handler
  useEffect(() => {
    if (!chartRef.current) return;
    const ro = new ResizeObserver(() => {
      chartInstance.current?.resize();
    });
    ro.observe(chartRef.current);
    return () => ro.disconnect();
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  const chartHeight = getChartHeight(config.visualType, apiData);

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '14px' }}>
      {/* Title */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{config.title}</h3>
        {apiData && (
          <span style={{ fontSize: '12px', color: '#6b7280' }}>
            {apiData.totalIncidents} incident{apiData.totalIncidents !== 1 ? 's' : ''}
            {apiData.totalIncidents !== apiData.totalIncidentsAll && ` of ${apiData.totalIncidentsAll}`}
          </span>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px', alignItems: 'flex-start' }}>
        <MultiSelect label="Year" options={yearOptions} selected={yearFilter} onChange={setYearFilter} />
        <MultiSelect label="Domain" options={domainOptions} selected={domainFilter} onChange={setDomainFilter} />
        <MultiSelect label="Subdomain" options={subdomainOptions} selected={subdomainFilter} onChange={setSubdomainFilter} />
        <MultiSelect label="Entity" options={entityOptions} selected={entityFilter} onChange={setEntityFilter} />
        <MultiSelect label="Intent" options={intentOptions} selected={intentFilter} onChange={setIntentFilter} />
      </div>

      {/* Severity filters (collapsible) */}
      {severityFields.length > 0 && (
        <details open={severityOpen} onToggle={(e: Event) => setSeverityOpen((e.target as HTMLDetailsElement).open)}>
          <summary style={{
            cursor: 'pointer',
            fontSize: '12px',
            color: '#6b7280',
            marginBottom: '8px',
            userSelect: 'none',
          }}>
            Severity Filters {Object.values(severityFilters).some((v) => v > 0) && '(active)'}
          </summary>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            <SeveritySelect
              label="Highest (any category)"
              paramKey="minSeverityHighest"
              value={severityFilters['minSeverityHighest'] || 0}
              onChange={(val) => setSeverityFilters((prev) => ({ ...prev, minSeverityHighest: val }))}
            />
            {severityFields.map((sf) => {
              const paramKey = `minSeverity${sf.key.replace('SeverityDirect', '').replace(/^./, (c) => c.toUpperCase())}`;
              return (
                <SeveritySelect
                  key={sf.key}
                  label={sf.label}
                  paramKey={paramKey}
                  value={severityFilters[paramKey] || 0}
                  onChange={(val) => setSeverityFilters((prev) => ({ ...prev, [paramKey]: val }))}
                />
              );
            })}
          </div>
        </details>
      )}

      {/* Chart */}
      {loading && <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>}
      {error && <div style={{ padding: '40px', textAlign: 'center', color: '#dc2626' }}>Error: {error}</div>}
      {!loading && !error && (
        <div ref={chartRef} style={{ width: '100%', height: `${chartHeight}px` }} />
      )}
    </div>
  );
}

// ============================================
// Chart height calculation
// ============================================

function getChartHeight(visualType: string, data: IncidentApiResponse | null): number {
  if (!data) return 400;
  switch (visualType) {
    case 'vertical-bar':
      return 400;
    case 'horizontal-bar': {
      const rowCount = data.domains?.length || 7;
      return Math.max(300, rowCount * 60 + 120);
    }
    case 'pie':
      return 420;
    default:
      return 400;
  }
}

// ============================================
// Vertical stacked bar (domain-by-year, subdomain-by-year)
// ============================================

function buildVerticalBarOption(
  data: IncidentApiResponse,
  chartTheme: ReturnType<typeof getChartTheme>,
  isMobile: boolean,
  config: IncidentChartConfig,
): echarts.EChartsOption {
  const years = data.years || [];
  const seriesData = data.series || [];
  const isDomainChart = config.apiChartType === 'domain-by-year';

  // Build subdomain index for palette colours
  const subdomainsByDomain = new Map<string, string[]>();
  if (!isDomainChart && data.subdomains) {
    for (const sd of data.subdomains) {
      const domainNum = sd.match(/^(\d+)\./)?.[1] || '0';
      if (!subdomainsByDomain.has(domainNum)) subdomainsByDomain.set(domainNum, []);
      subdomainsByDomain.get(domainNum)!.push(sd);
    }
  }

  const series: echarts.BarSeriesOption[] = seriesData.map((s) => {
    let color: string;
    if (isDomainChart) {
      color = getDomainColor(s.name);
    } else {
      const domainNum = s.name.match(/^(\d+)\./)?.[1] || '0';
      const siblings = subdomainsByDomain.get(domainNum) || [];
      const idx = siblings.indexOf(s.name);
      color = getSubdomainPaletteColor(s.name, idx >= 0 ? idx : 0);
    }

    return {
      name: s.name,
      type: 'bar',
      stack: 'total',
      data: s.data,
      itemStyle: { color },
      emphasis: { focus: 'series' },
    };
  });

  // Compute max for y-axis
  const maxPerYear = years.map((_, yi) =>
    seriesData.reduce((sum, s) => sum + (s.data[yi] || 0), 0)
  );
  const maxVal = Math.max(...maxPerYear, 1);

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: chartTheme.tooltipBg,
      borderColor: chartTheme.tooltipBorder,
      textStyle: { color: chartTheme.tooltipText, fontSize: 12 },
    },
    legend: {
      type: 'scroll',
      bottom: 0,
      textStyle: { color: chartTheme.text, fontSize: 11 },
      pageTextStyle: { color: chartTheme.text },
      itemWidth: 14,
      itemHeight: 10,
    },
    grid: {
      left: isMobile ? 40 : 50,
      right: 20,
      top: 20,
      bottom: isDomainChart ? 80 : 120,
      containLabel: false,
    },
    xAxis: {
      type: 'category',
      data: years,
      axisLabel: {
        color: chartTheme.text,
        fontSize: isMobile ? 10 : 12,
      },
      axisLine: { lineStyle: { color: chartTheme.grid } },
      axisTick: { lineStyle: { color: chartTheme.grid } },
      name: 'Year',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: chartTheme.muted, fontSize: 12 },
    },
    yAxis: {
      type: 'value',
      max: niceMax(maxVal),
      axisLabel: {
        color: chartTheme.text,
        fontSize: isMobile ? 10 : 12,
        formatter: (v: number) => String(Math.round(v)),
      },
      splitLine: { lineStyle: { color: chartTheme.grid, type: 'dashed' } },
    },
    series,
    animationDuration: 400,
  };
}

// ============================================
// Horizontal stacked bar (domain/subdomain)
// ============================================

function buildHorizontalBarOption(
  data: IncidentApiResponse,
  chartTheme: ReturnType<typeof getChartTheme>,
  isMobile: boolean,
): echarts.EChartsOption {
  const domains = data.domains || [];
  const subdomains = data.subdomains || [];
  const seriesData = data.series || [];
  const domainTotals = data.domainTotals || [];

  // Build subdomain-to-index-in-domain map for palette
  const subdomainsByDomain = new Map<string, string[]>();
  for (const sd of subdomains) {
    const domainNum = sd.match(/^(\d+)\./)?.[1] || '0';
    if (!subdomainsByDomain.has(domainNum)) subdomainsByDomain.set(domainNum, []);
    subdomainsByDomain.get(domainNum)!.push(sd);
  }

  const series: echarts.BarSeriesOption[] = seriesData.map((s) => {
    const domainNum = s.name.match(/^(\d+)\./)?.[1] || '0';
    const siblings = subdomainsByDomain.get(domainNum) || [];
    const idx = siblings.indexOf(s.name);
    const color = getSubdomainPaletteColor(s.name, idx >= 0 ? idx : 0);

    return {
      name: s.name,
      type: 'bar',
      stack: 'total',
      data: s.data,
      itemStyle: { color },
      emphasis: { focus: 'series' },
      label: { show: false },
    };
  });

  const maxTotal = Math.max(...domainTotals, 1);
  const labelWidth = isMobile ? 100 : 240;

  // Truncate domain labels for display
  const displayLabels = domains.map((d) => {
    const maxLen = isMobile ? 20 : 38;
    return d.length > maxLen ? d.slice(0, maxLen - 1) + '...' : d;
  });

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: chartTheme.tooltipBg,
      borderColor: chartTheme.tooltipBorder,
      textStyle: { color: chartTheme.tooltipText, fontSize: 12 },
    },
    legend: {
      type: 'scroll',
      bottom: 0,
      textStyle: { color: chartTheme.text, fontSize: 10 },
      pageTextStyle: { color: chartTheme.text },
      itemWidth: 14,
      itemHeight: 10,
    },
    grid: {
      left: labelWidth + 20,
      right: 60,
      top: 10,
      bottom: 80,
      containLabel: false,
    },
    xAxis: {
      type: 'value',
      max: niceMax(maxTotal),
      axisLabel: {
        color: chartTheme.text,
        fontSize: isMobile ? 10 : 12,
        formatter: (v: number) => String(Math.round(v)),
      },
      splitLine: { lineStyle: { color: chartTheme.grid, type: 'dashed' } },
      name: 'Incident Count',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: chartTheme.muted, fontSize: 12 },
    },
    yAxis: {
      type: 'category',
      data: displayLabels,
      inverse: true,
      axisLabel: {
        color: chartTheme.text,
        fontSize: isMobile ? 9 : 11,
        width: labelWidth,
        overflow: 'truncate',
      },
      axisLine: { lineStyle: { color: chartTheme.grid } },
      axisTick: { show: false },
    },
    // Show totals as graphic text to the right of bars
    graphic: domainTotals.map((total, i) => ({
      type: 'text' as const,
      left: `${((total / niceMax(maxTotal)) * 100 * 0.75) + 25}%`,
      top: `${10 + ((i + 0.5) / domains.length) * (100 - 90 / domains.length)}%`,
      style: {
        text: String(total),
        fill: chartTheme.muted,
        fontSize: 11,
      },
    })),
    series,
    animationDuration: 400,
  };
}

// ============================================
// Pie chart (domain proportion)
// ============================================

function buildPieOption(
  data: IncidentApiResponse,
  chartTheme: ReturnType<typeof getChartTheme>,
  isMobile: boolean,
): echarts.EChartsOption {
  const pieData = (data.data || []).map((d) => ({
    ...d,
    itemStyle: { color: getDomainColor(d.name) },
  }));

  const total = pieData.reduce((sum, d) => sum + d.value, 0);

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: chartTheme.tooltipBg,
      borderColor: chartTheme.tooltipBorder,
      textStyle: { color: chartTheme.tooltipText, fontSize: 12 },
      formatter: (params: any) => {
        const p = params as { name: string; value: number; percent: number };
        return `${p.name}<br/>Count: ${p.value}<br/>Proportion: ${p.percent.toFixed(1)}%`;
      },
    },
    legend: {
      orient: isMobile ? 'horizontal' : 'vertical',
      right: isMobile ? 'center' : 20,
      top: isMobile ? 'bottom' : 'middle',
      textStyle: { color: chartTheme.text, fontSize: 11 },
      itemWidth: 14,
      itemHeight: 10,
    },
    series: [
      {
        type: 'pie',
        radius: isMobile ? ['30%', '55%'] : ['35%', '60%'],
        center: isMobile ? ['50%', '40%'] : ['35%', '50%'],
        data: pieData,
        label: {
          show: true,
          formatter: (params: any) => {
            const pct = ((params.value / total) * 100).toFixed(1);
            return `${pct}%`;
          },
          fontSize: 11,
          color: chartTheme.text,
        },
        labelLine: { show: true },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.3)',
          },
        },
      },
    ],
    animationDuration: 400,
  };
}

// ============================================
// MultiSelect filter component
// ============================================

interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (val: string[]) => void;
}

function MultiSelect({ label, options, selected, onChange }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!options.length) return null;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          padding: '4px 10px',
          fontSize: '12px',
          border: '1px solid #e5e7eb',
          borderRadius: '4px',
          background: selected.length ? '#e5e7eb' : '#fff',
          cursor: 'pointer',
          color: '#1f2937',
          whiteSpace: 'nowrap',
        }}
      >
        {label}{selected.length ? ` (${selected.length})` : ''} ▾
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          zIndex: 100,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: '250px',
          overflowY: 'auto',
          minWidth: '180px',
          padding: '4px 0',
        }}>
          {selected.length > 0 && (
            <button
              onClick={() => { onChange([]); setOpen(false); }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '4px 10px',
                fontSize: '11px',
                color: '#6b7280',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              Clear all
            </button>
          )}
          {options.map((opt) => {
            const isSelected = selected.includes(opt);
            return (
              <label
                key={opt}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '3px 10px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  background: isSelected ? '#f3f4f6' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {
                    if (isSelected) {
                      onChange(selected.filter((s) => s !== opt));
                    } else {
                      onChange([...selected, opt]);
                    }
                  }}
                  style={{ margin: 0 }}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
                  {opt}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================
// Severity select component
// ============================================

interface SeveritySelectProps {
  label: string;
  paramKey: string;
  value: number;
  onChange: (val: number) => void;
}

function SeveritySelect({ label, paramKey, value, onChange }: SeveritySelectProps) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
      <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>{label} ≥</span>
      <select
        value={value}
        onChange={(e) => onChange(parseInt((e.target as HTMLSelectElement).value, 10))}
        style={{
          padding: '2px 4px',
          fontSize: '12px',
          border: '1px solid #e5e7eb',
          borderRadius: '3px',
          background: value > 0 ? '#e5e7eb' : '#fff',
        }}
      >
        <option value={0}>Any</option>
        <option value={1}>1</option>
        <option value={2}>2</option>
        <option value={3}>3</option>
        <option value={4}>4</option>
        <option value={5}>5</option>
      </select>
    </div>
  );
}
